import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { LazyLoadEvent } from "primeng/api";
import { FileUpload } from "primeng/fileUpload";
import { Table } from "primeng/table";
import { BehaviorSubject } from "rxjs";
import { BaseViewComponent } from "../../../../../../shared/core/interfaces/baseViewComponent";
import { LookupService } from "../../../../../../shared/core/services/lookup.service";
import { ToastService } from "../../../../../../shared/core/services/toast.service";
import { FileUploadDataItem } from "../../../core-rms/file-upload-data-item";
import { FileService } from "../../../core-rms/file.service";
import { RmsPolicyPermissionsService } from 'projects/rms/app/shared/core-rms/rms-policy-permissions.service';
import { UserPreferencesService } from "projects/shared/core/services/user-preferences.service";

@Component({
  selector: "rms-file-upload",
  templateUrl: "./rms-file-upload.component.html",
  styleUrls: ["./rms-file-upload.component.css"]
})
export class RmsFileUploadComponent extends BaseViewComponent implements OnInit {
  @ViewChild("dtFiles") dataTable: Table;
  @Output() afterUpload = new EventEmitter<any>();
  @Output() afterDelete = new EventEmitter<any>();
  @Input() set showInBrowser(value: any[]) {
    if (value && value.length > 0) {
      this._showInBrowser = value;
    }
  }
  @Input() set showAllInBrowser(value: boolean) {
    this._showAllInBrowser = value;
  }
  @Input() set ownerId(value: number) {
    if (value) {
      this.parentId = value;
    }
  }
  @Input() set respId(value: number) {
    if (value) {
      this.responderId = value;
    }
  }
  @Input() set ownerFolderName(value: string) {
    if (value) {
      this.folderName = value;
    }
  }
  @Input() set ownerApiPath(value: string) {
    if (value) {
      this.parentApiPath = value;
    }
  }
  @Input() set fileDocumentsService(value: FileService) {
    if (value) {
      this._documentService = value;
    }
  }
  @Input() set displayTypeColumn(value: boolean) {
    if (value) {
      this._displayTypeColumn = value;
      this.fileColumns = (this._displayTypeColumn)
        ? [
          { header: "File Name", field: "documentName" },
          { header: "Document Type", field: "documentType" },
          { header: "Description", field: "documentDescription" },
          { header: "Download", field: undefined },
          { header: "Created By", field: "createdBy" },
          { header: "Created Date", field: "createdDate" }
        ]
        : [
          { header: "File Name", field: "documentName" },
          { header: "Description", field: "documentDescription" },
          { header: "Download", field: undefined },
          { header: "Created By", field: "createdBy" },
          { header: "Created Date", field: "createdDate" }
        ];
    }
  }
  @Input() rootDocumentName: string;

  private _documentService: FileService = null;
  private parentId = 0;
  private responderId = 0;
  private parentApiPath = "";
  private folderName = "";
  private _displayTypeColumn = false;
  private _showAllInBrowser = false;
  private _showInBrowser: any[] = [];

  fileColumns: any[] = [
    { header: "File Name", field: "documentName" },
    { header: "Description", field: "documentDescription" },
    { header: "Download", field: undefined },
    { header: "Created By", field: "createdBy" },
    { header: "Created Date", field: "createdDate" }
  ];
  formGroupFile: FormGroup;
  modelError = "";
  isSavingOrLoadingData = true;
  recordCount: number;
  first = 0;
  pageNumber = 0;
  pageSizeOptions = [10, 25, 50];
  pageSize = null;
  tablePrefKey = "fileUploadPageSize";
  firstElement = 0;
  sortInfo = [{ "columnName": "UpdatedOn", "isDescending": true }];

  private _uploadedFiles$ = new BehaviorSubject<FileUploadDataItem[]>([]);
  get uploadedFiles$() {
    return this._uploadedFiles$.asObservable();
  }
  get uploadedFiles() {
    return this._uploadedFiles$.value;
  }

  private _documentTypes$ = new BehaviorSubject<any[]>([]);
  get documentTypes$() {
    return this._documentTypes$.asObservable();
  }
  get documentTypes() {
    return this._documentTypes$.value;
  }

  get canUpload() {
    return this._policyPermissionsService.hasPermission("Documents:Upload");
  }
  get canDelete() {
    return this._policyPermissionsService.hasPermission("Documents:Delete");
  }

  get displayTypeColumn() {
    return this._displayTypeColumn;
  }

  constructor(lookupService: LookupService,
    private _formBuilder: FormBuilder,
    private _toastService: ToastService,
    private _userPreferencesService: UserPreferencesService,
    private _policyPermissionsService: RmsPolicyPermissionsService) {
    super(lookupService);
  }

  ngOnInit() {

    this.formGroupFile = this._formBuilder.group({
      documentTypeId: ["", Validators.required],
      documentDescription: [""]
    });

    this._documentService.getFilteredDocumentTypes()
      .then((res: any[]) => {
        this._documentTypes$.next(res);

        if (this.documentTypes.length === 1) {
          this.formGroupFile.get("documentTypeId").patchValue(this.documentTypes[0].id);
        } else {
          this.formGroupFile.get("documentTypeId").patchValue("");
        }
      }, () => this._documentTypes$.next([]));

    this._userPreferencesService.get(this.tablePrefKey).subscribe(data => {
      this.pageSize = +data?.preferenceValue || 10;
      this.onReloadList({ rows: this.pageSize, first: 0 })			//not always needed, but when needed will be bespoke
    });

    this.safeSubscribe(FileService.getMessage(),
      msg => {
        if (msg) {
          this.loadAllDocuments();
        }
      }
    );

  }

  onDownloadClick(file) {
    this.safeSubscribe(this._documentService.exportData(file), (response) => {
      if (response.body.size === 0) {
        return this._toastService.showError("File not found.");
      }
      let fileData = new File([response.body], response.headers.get('x-fileName'), { type: response.headers.get('content-type') });
      let url = URL.createObjectURL(fileData);
      let urlLink = document.createElement('a');
      urlLink.href = url;
      urlLink.setAttribute('target', '_blank');
      urlLink.setAttribute('download', file.documentDisplayName);
      document.body.appendChild(urlLink);
      urlLink.click();
      document.body.removeChild(urlLink);
      URL.revokeObjectURL(url);
    }, error => this._toastService.showError(error));
  }

  onReloadList(event: LazyLoadEvent) {
    if (!this.pageSize) {
      return; //cancel the load, which will get re-triggered when userPreferences completes
    }

    if (event.rows != this.pageSize) {
      if (!(this.pageSizeOptions.find(x => x === event.rows))) event.rows = 50; //if pageSize is not one of the available options, then the user's pick was the "all" option
      this._userPreferencesService.set(this.tablePrefKey, event.rows + '');
      this.pageSize = event.rows;
    }

    this.firstElement = event.first;
    this.pageNumber = this.firstElement / event.rows;
    this.pageSize = event.rows;
    const direction = event.sortOrder === -1 ? true : false;

    switch (event.sortField) {
      case "documentName":
        this.sortInfo = [
          {
            columnName: (this.rootDocumentName ? (this.rootDocumentName + ".") : "") + "Name",
            isDescending: direction
          }
        ];
        break;
      case "documentType":
        this.sortInfo = [
          {
            columnName: "DocumentType.Name",
            isDescending: direction
          }
        ];
        break;
      case "documentDescription":
        this.sortInfo = [
          {
            columnName: (this.rootDocumentName ? (this.rootDocumentName + ".") : "") + "Description",
            isDescending: direction
          }
        ];
        break;
      case "createdBy":
        this.sortInfo = [
          {
            columnName: (this.rootDocumentName ? (this.rootDocumentName + ".") : "") + "CreatedBy.FirstName",
            isDescending: direction
          },
          {
            columnName: (this.rootDocumentName ? (this.rootDocumentName + ".") : "") + "CreatedBy.LastName",
            isDescending: direction
          }
        ];
        break;
      case "createdDate":
        this.sortInfo = [
          {
            columnName: (this.rootDocumentName ? (this.rootDocumentName + ".") : "") + "UpdatedOn",
            isDescending: direction
          }
        ];
        break;
    }
    this.loadAllDocuments();
  }

  onUploadHandler(event, uploader: FileUpload) {
    if (this.formGroupFile.invalid) {
      Object.keys(this.formGroupFile.controls).forEach(key => {
        this.formGroupFile.controls[key].markAsDirty();
      });
    } else {
      const files = event.files.map(f => f);
      this.uploadFile(files, uploader).then(() => this.refreshForm());
    }
  }

  async onDataExportClick(file) {
    const response = await this._documentService.exportData(file).toPromise();
    if (response.body.size === 0) {
      return this._toastService.showError("File not found.");
    }
    const fileData = new File([response.body], response.headers.get('x-fileName'), { type: response.headers.get('content-type') });
    const url = URL.createObjectURL(fileData);

    if (!this._showAllInBrowser) {
      let matches = /.*(?<extension>\.[a-z]{1,})$/gi.exec(file.documentName);
      let hasMatches = matches && matches.groups && undefined !== matches.groups["extension"];

      if (hasMatches && this._showInBrowser.length > 0 && this._showInBrowser.includes(matches.groups["extension"])) {
        window.open(url, "_blank", "popup=false,location=no,titlebar=no,status=no");
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('target', '_blank');
        link.setAttribute('download', response.headers.get('x-fileName'));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      window.open(url, "_blank", "popup=false,location=no,titlebar=no,status=no");
    }
    URL.revokeObjectURL(url);
  }

  onDeleteFileClick(file) {
    if (confirm(`Are you sure you would like to delete this document?`)) {
      this.isSavingOrLoadingData = true;
      const formData = new FormData();
      formData.append("documentTypeId", file.documentTypeId);
      formData.append("parentDocumentId", file.parentDocumentId);
      formData.append("responderDocumentId", file.responderDocumentId);

      return this._documentService.deleteFile(`${this.parentApiPath}deletefile`, formData)
        .then(data => {
          this.afterDelete.emit(data);
          this.dataTable.clear();
        })
        .catch((error) => {
          this.isSavingOrLoadingData = false;
          if (error.modelErrors) {
            this.modelError = error.modelErrors;
          } else {
            this._toastService.showError(error);
          }
        })
        .finally(() => {
          this.isSavingOrLoadingData = false;
        });
    };
  }

  loadAllDocuments() {
    this.modelError = "";
    this.isSavingOrLoadingData = true;
    return this._documentService.loadDocuments(this.parentId, this.pageNumber, this.pageSize, this.sortInfo)
      .then(result => {
        this._uploadedFiles$.next(result.fileUploadDataItemArray as FileUploadDataItem[]);
        this.recordCount = result.totalRecordCount;
      })
      .catch((error) => {
        this.isSavingOrLoadingData = false;
        this._toastService.showError(error);
      })
      .finally(() => {
        this.isSavingOrLoadingData = false;
      });
  }

  refreshForm() {
    this.formGroupFile.reset();
    this.formGroupFile.get("documentDescription").patchValue("");
    if (this.documentTypes.length === 1) {
      this.formGroupFile.get("documentTypeId").patchValue(this.documentTypes[0].id);
    } else {
      this.formGroupFile.get("documentTypeId").patchValue("");
    }
  }

  uploadFile = (files, uploader: FileUpload) => {
    if (files.length === 0) {
      return;
    }
    const fileToUpload = files[0] as File;
    const formData = new FormData();
    formData.append("formFile", fileToUpload, fileToUpload.name);
    formData.append("folderName", this.folderName);
    formData.append("responderDocumentId", "0");
    formData.append("parentDocumentId", "0");
    formData.append("documentTypeId", this.formGroupFile.get("documentTypeId").value);
    formData.append("documentDescription", this.formGroupFile.get("documentDescription").value);
    formData.append("responderId", this.responderId.toString());
    formData.append("parentId", this.parentId.toString());
    this.removeFile(files[0], uploader);
    this.isSavingOrLoadingData = true;

    return this._documentService.uploadFile(`${this.parentApiPath}uploadfile`, formData)
      .then(data => {
        this.afterUpload.emit(data);
        this.dataTable.clear();
        this.refreshForm();
      })
      .catch((error) => {
        this.isSavingOrLoadingData = false;
        if (error.modelErrors) {
          this.modelError = error.modelErrors;
        } else {
          this._toastService.showError(error);
        }
      })
      .finally(
        () => this.isSavingOrLoadingData = false);
  };

  deleteFile(file) {
    const formData = new FormData();
    formData.append("documentTypeId", file.documentTypeId);
    formData.append("parentDocumentId", file.parentDocumentId);
    formData.append("responderDocumentId", file.responderDocumentId);
    this.isSavingOrLoadingData = true;
    return this._documentService.deleteFile(`${this.parentApiPath}deletefile`, formData)
      .then(data => {
        this.afterDelete.emit(data);
        this.dataTable.clear();
      })
      .catch((error) => {
        this.isSavingOrLoadingData = false;
        if (error.modelErrors) {
          this.modelError = error.modelErrors;
        } else {
          this._toastService.showError(error);
        }
      })
      .finally(() => {
        this.isSavingOrLoadingData = false;
      });
  }

  getSizeInMegaBytes(file: File) {
    return file ? file.size / 1000000 : 0;
  }

  removeFile(file: File, uploader: FileUpload) {
    const index = uploader.files.indexOf(file);
    uploader.files.splice(index, 1);
  }
}
